<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">智能行銷精靈</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="$router.push({ name: 'SmartMarketingList' })">
          返回推薦列表
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading || submitting" @click="handleSubmit">
          {{ submitting ? '建立中...' : '建立並進入編輯器' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <form v-else class="wizard-form" @submit.prevent="handleSubmit">
      <div class="form-section">
        <h2>{{ recommendation?.recommendationName || '建立 Campaign' }}</h2>
        <p class="hint">{{ recommendation?.reason }}</p>

        <div class="form-group">
          <label class="form-label">活動名稱</label>
          <input v-model="form.campaignName" type="text" class="form-control" required />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">開始日期</label>
            <input v-model="form.startAt" type="date" class="form-control" required />
          </div>
          <div class="form-group">
            <label class="form-label">結束日期</label>
            <input v-model="form.endAt" type="date" class="form-control" required />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">預算上限（元，僅提醒）</label>
            <input v-model.number="form.budgetCap" type="number" min="0" class="form-control" />
            <small class="form-text">不會阻擋建立或停發活動；僅用於超標提醒。</small>
          </div>
          <div class="form-group">
            <label class="form-label">轉換歸因天數</label>
            <input v-model.number="form.conversionWindowDays" type="number" min="1" class="form-control" />
          </div>
        </div>

        <details class="field-help">
          <summary>
            <i class="ri-information-line"></i>
            <span>轉換歸因天數說明</span>
            <i class="ri-arrow-down-s-line field-help-chevron"></i>
          </summary>
          <div class="field-help-body">
            <p>從這位顧客進入活動的那一天起算。在這段天數內完成的訂單，實付金額會算進這檔活動的轉換營收，並用來計算 ROI。</p>
            <p>設 14 天時，顧客進入活動後 14 天內的訂單會計入，第 15 天起的訂單不計入。</p>
            <p>同一筆訂單若也符合其他活動，那些活動會各自計入一次。</p>
          </div>
        </details>

        <div v-if="playbook?.requiredBindings?.length" class="bindings-block">
          <h3>待填資源</h3>
          <div v-for="binding in playbook.requiredBindings" :key="binding.bindingKey" class="form-group">
            <label class="form-label">{{ binding.label }}</label>
            <input
              v-if="binding.resourceType === 'spend_threshold'"
              v-model.number="resourceSelections[binding.bindingKey]"
              type="number"
              min="1"
              step="1"
              class="form-control"
              required
              placeholder="請輸入滿額門檻金額"
            />
            <select
              v-else
              v-model="resourceSelections[binding.bindingKey]"
              class="form-control"
              :required="['coupon_template', 'line_msg_template', 'questionnaire'].includes(binding.resourceType)"
            >
              <option value="">請選擇</option>
              <option
                v-for="opt in bindingOptions[binding.resourceType] || []"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="costEstimate" class="estimate-box">
          <h3>預估成本（參考）</h3>
          <p>受眾 {{ costEstimate.audienceSize?.toLocaleString() }} 人 · 合計 ${{ formatMoney(costEstimate.totalCost) }}</p>
          <p class="sub">
            LINE {{ formatPushCost(costEstimate.lineMessageCost) }} ·
            點數 ${{ formatMoney(costEstimate.pointsCost) }} ·
            券 ${{ formatMoney(costEstimate.couponCost) }}
          </p>
          <p v-if="form.budgetCap && costEstimate.totalCost > form.budgetCap" class="warn">
            預估成本超過預算上限（提醒用，不會阻擋建立）
          </p>
        </div>
      </div>

    </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { smartMarketingService } from '@/api/modules/smartMarketing.js';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { couponTemplateService } from '@/api/modules/couponTemplates.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { questionnaireService } from '@/api/modules/questionnaires.js';
import { showError, showSuccess } from '@/utils/swal.js';
import { formatPushCost } from '@/constants/marketingCampaign.js';

function toDateStartIso(value) {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toISOString();
}

function toDateEndIso(value) {
  if (!value) return null;
  return new Date(`${value}T23:59:59.999`).toISOString();
}

function defaultEndDate() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

export default {
  name: 'SmartMarketingWizard',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const scenario = route.query.scenario || 'winback';
    const loading = ref(true);
    const submitting = ref(false);
    const recommendation = ref(null);
    const playbook = ref(null);
    const costEstimate = ref(null);
    const resourceSelections = reactive({});
    const bindingOptions = reactive({
      coupon_template: [],
      line_msg_template: [],
      questionnaire: [],
      points_amount: [{ id: '100', label: '100 點' }, { id: '50', label: '50 點' }]
    });

    const form = ref({
      campaignName: '',
      startAt: new Date().toISOString().slice(0, 10),
      endAt: defaultEndDate(),
      budgetCap: null,
      conversionWindowDays: 14
    });

    const buildResourceBindings = () => {
      const bindings = {};
      for (const binding of playbook.value?.requiredBindings || []) {
        const selected = resourceSelections[binding.bindingKey];
        if (!selected) continue;
        if (['coupon_template', 'line_msg_template', 'questionnaire'].includes(binding.resourceType)) {
          bindings[binding.bindingKey] = {
            mode: 'existing',
            resourceType: binding.resourceType,
            resourceId: selected
          };
        } else if (binding.resourceType === 'points_amount') {
          bindings[binding.bindingKey] = {
            mode: 'create',
            resourceType: binding.resourceType,
            payload: { amount: Number(selected) }
          };
        } else if (binding.resourceType === 'spend_threshold') {
          bindings[binding.bindingKey] = {
            mode: 'create',
            resourceType: binding.resourceType,
            payload: { amount: Number(selected) }
          };
        }
      }
      return bindings;
    };

    const refreshEstimate = async () => {
      if (!playbook.value) return;
      try {
        const res = await smartMarketingService.estimateCost({
          playbookCode: playbook.value.playbookCode,
          recommendationScenario: scenario,
          estimatedAudience: recommendation.value?.estimatedAudience || 0,
          resourceBindings: buildResourceBindings()
        });
        costEstimate.value = res.data?.data ?? res.data;
      } catch {
        costEstimate.value = null;
      }
    };

    const load = async () => {
      loading.value = true;
      try {
        const [detailRes, couponRes, lineRes, questionnaireRes] = await Promise.all([
          smartMarketingService.getRecommendationDetail(scenario),
          couponTemplateService.getCouponTemplates({ pageSize: 100, isActive: true }),
          lineMsgTemplateService.list({ pageSize: 100, status: 'active' }),
          questionnaireService.list({ page: 1, limit: 100, status: 'published' })
        ]);
        const detail = detailRes.data?.data ?? detailRes.data;
        recommendation.value = detail;
        playbook.value = detail?.playbook || null;
        form.value.campaignName = detail?.recommendationName
          ? `${detail.recommendationName} ${new Date().toISOString().slice(0, 10)}`
          : '';
        form.value.conversionWindowDays = detail?.defaultConversionWindowDays || 14;
        // 預算上限僅作提醒，不自動帶入，避免被誤解為硬限制。
        form.value.budgetCap = null;

        const couponData = couponRes.data?.data ?? couponRes.data;
        const lineData = lineRes.data?.data ?? lineRes.data;
        bindingOptions.coupon_template = (couponData?.items || []).map((t) => ({
          id: t.id,
          label: t.templateName || t.name
        }));
        bindingOptions.line_msg_template = (lineData?.items || lineData || []).map((t) => ({
          id: t.id,
          label: t.templateName || t.name
        }));
        const questionnaireData = questionnaireRes.data?.data ?? questionnaireRes.data;
        bindingOptions.questionnaire = (questionnaireData?.items || []).map((item) => ({
          id: item.id,
          label: item.name
        }));

        for (const binding of playbook.value?.requiredBindings || []) {
          if (binding.resourceType === 'spend_threshold' && resourceSelections[binding.bindingKey] == null) {
            resourceSelections[binding.bindingKey] = 1000;
          }
        }

        await refreshEstimate();
      } catch (e) {
        showError(e.response?.data?.message || e.message || '載入失敗');
      } finally {
        loading.value = false;
      }
    };

    watch(resourceSelections, refreshEstimate, { deep: true });

    const handleSubmit = async () => {
      submitting.value = true;
      try {
        const resourceBindings = buildResourceBindings();
        const payload = {
          playbookCode: playbook.value?.playbookCode || recommendation.value?.playbookCode,
          campaign: {
            campaignName: form.value.campaignName,
            startAt: toDateStartIso(form.value.startAt),
            endAt: toDateEndIso(form.value.endAt)
          },
          resourceBindings,
          smartMarketing: {
            recommendationScenario: scenario,
            recommendationType: recommendation.value?.recommendationType || 'journey',
            recommendationReason: recommendation.value?.reason,
            estimatedAudience: recommendation.value?.estimatedAudience,
            estimatedCost: costEstimate.value?.totalCost,
            riskFlags: recommendation.value?.riskFlags?.map((r) => r.code) || [],
            wizardFormPayload: {
              budgetCap: form.value.budgetCap,
              conversionWindowDays: form.value.conversionWindowDays,
              resourceBindings
            }
          },
          tracking: {
            attributionConfig: {
              conversionWindowDays: form.value.conversionWindowDays,
              revenueBasis: 'paidAmount'
            }
          },
          budgetCap: form.value.budgetCap,
          creationSource: 'smart_marketing'
        };
        const res = await marketingCampaignService.instantiateFromPlaybook(payload);
        const data = res.data?.data ?? res.data;
        showSuccess('Campaign 草稿建立成功');
        router.push({ name: 'EditMarketingCampaign', params: { id: data.campaign.id } });
      } catch (e) {
        showError(e.response?.data?.message || e.message || '建立失敗');
      } finally {
        submitting.value = false;
      }
    };

    const formatMoney = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return num.toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    };

    onMounted(load);

    return {
      loading,
      submitting,
      recommendation,
      playbook,
      form,
      resourceSelections,
      bindingOptions,
      costEstimate,
      handleSubmit,
      formatMoney,
      formatPushCost
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.wizard-form {
  max-width: 760px;
}

.form-section {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
}

.form-section h2 {
  margin: 0 0 8px;
}

.hint {
  color: #6c757d;
  margin-bottom: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.bindings-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.estimate-box {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.estimate-box h3 {
  margin: 0 0 8px;
  font-size: 15px;
}

.estimate-box .sub {
  font-size: 13px;
  color: #6c757d;
}

.estimate-box .warn {
  color: #b45309;
  margin-bottom: 0;
}

.form-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6c757d;
}

.field-help {
  margin-top: 8px;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 6px;
  color: #1e3a5f;
}

.field-help summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  cursor: pointer;
  list-style: none;
  font-size: 13px;
  font-weight: 600;
}

.field-help summary::-webkit-details-marker {
  display: none;
}

.field-help summary > .ri-information-line {
  color: #0d6efd;
  font-size: 16px;
}

.field-help-chevron {
  margin-left: auto;
  font-size: 18px;
  color: #0d6efd;
  transition: transform 0.15s ease;
}

.field-help[open] .field-help-chevron {
  transform: rotate(180deg);
}

.field-help-body {
  padding: 0 10px 10px 32px;
}

.field-help-body p {
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.55;
  font-weight: 400;
}

.field-help-body p:last-child {
  margin-bottom: 0;
}
</style>
